import React, { FunctionComponent } from 'react';
import { Statistic, Icon, Popup } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { FormattedNumber } from 'react-intl';

import { ClassProp } from 'helpers/types';

import styles from './KeyMetricStat.styles';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  label: string;
  keyMetric: any;
  Component?: any;
  options?: FormattedNumber.PropsBase;
  info?: string;
  defaultText?: string;
  showDeltas: boolean;
  secondaryText?: string;
};

const formattedAmount = (value: number, options?: FormattedNumber.PropsBase) =>
  (value && options && options.style) === 'percent' ? value / 100 : value;

const calcDelta = (amount: number, previousAmount: number) => {
  const currValue = amount === 0 ? 0.0001 : amount;
  return ((currValue - previousAmount) / currValue) * 100;
};

const renderDeltas = (amount: number, previousAmount: number) => {
  const direction = amount > previousAmount ? 'up' : 'down';
  if (previousAmount === 0) return <span>&nbsp;</span>;

  return (
    <>
      <span data-direction={`${direction}`}>
        {
          //@ts-expect-error Icon
          <Icon name={`caret ${direction}`} />
        }
        <FormattedNumber
          value={calcDelta(amount, previousAmount)}
          style="decimal"
          maximumFractionDigits={1}
        />
        %
      </span>
    </>
  );
};

const renderSecondaryText = (
  classes: Styles,
  amountCleared: number,
  options?: FormattedNumber.PropsBase,
  Component?: any,
  secondaryText?: string,
) => {
  if (amountCleared === 0) return <span>&nbsp;</span>;

  return (
    <>
      <Component
        value={formattedAmount(amountCleared, options)}
        {...options}
        isSecondary
      />
      <span className={classes.secondaryText}> {secondaryText}</span>
    </>
  );
};

const KeyMetricStat: FunctionComponent<Props> = ({
  classes,
  label,
  keyMetric,
  Component,
  options,
  info,
  showDeltas,
  defaultText,
  secondaryText,
}) => {
  const { amount, amountCleared, previousAmount } = keyMetric;

  const onClickIcon = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Statistic>
      <Statistic.Label className={classes.label}>
        {label}

        <Popup
          onOpen={onClickIcon}
          content={info}
          size="tiny"
          on="click"
          hideOnScroll
          closeOnTriggerMouseLeave
          trigger={<Icon className={classes.icon} name="info circle" />}
        />
      </Statistic.Label>

      <Statistic.Value
        className={classes.value}
        title={amount.toLocaleString()}
      >
        {!!amount ? (
          <Component value={formattedAmount(amount, options)} {...options} />
        ) : defaultText ? (
          <span className={classes.defaultText}>{defaultText}</span>
        ) : (
          <span>Pending</span>
        )}
      </Statistic.Value>

      <Statistic.Label className={classes.secondaryData}>
        {showDeltas
          ? renderDeltas(amount, previousAmount)
          : renderSecondaryText(
              classes,
              amountCleared,
              options,
              Component,
              secondaryText,
            )}
      </Statistic.Label>
    </Statistic>
  );
};

export default withStyles(styles)(KeyMetricStat);
